import React from 'react';
import RequireLogin from './components/RequireLogin';
import AppHeader from './components/AppHeader';
import AppFooter from './components/AppFooter';
import HomePage from './components/DevToolsBanner';
import LandingPage from './components/LandingPage';
import CoverageDashboard from './components/CoverageDashboard';
import LanguageDetail from './components/LanguageDetail';
import ConventionsBrowser from './components/ConventionsBrowser';
import ConventionsDetail from './components/ConventionsDetail';
import TranslationBrowser from './components/TranslationBrowser';
import MessageDetail from './components/MessageDetail';
import GlossarySearch from './components/GlossarySearch';
import VerificationDetail from './components/VerificationDetail';
import QCDashboard from './components/QCDashboard';
import QCFindingsBrowser from './components/QCFindingsBrowser';
import ChangelogFeed from './components/ChangelogFeed';
import QCReviewDashboard from './components/QCReviewDashboard';
import QCReviewQueue from './components/QCReviewQueue';
import NavGroupProposalsBrowser from './components/NavGroupProposalsBrowser';
import DocBrowser from './components/DocBrowser';
import DocTimeline from './components/DocTimeline';
import DocViewer from './components/DocViewer';
import DocEditor from './components/DocEditor';
import DocDivergence from './components/DocDivergence';
import DocHistory from './components/docs/DocHistory';
import DocVersionView from './components/docs/DocVersionView';
import DocDraftReview from './components/docs/DocDraftReview';
import UserTools from './components/UserTools';
import QmsDashboard from './components/qms/QmsDashboard';
import DocumentRegister from './components/qms/DocumentRegister';
import DocumentDetail from './components/qms/DocumentDetail';
import DocumentEditor from './components/qms/DocumentEditor';
import DocumentCreate from './components/qms/DocumentCreate';
import ReviewSchedule from './components/qms/ReviewSchedule';
import ReviewQueue from './components/qms/ReviewQueue';
import FailureModes from './components/qms/FailureModes';
import FailureModeDetail from './components/qms/FailureModeDetail';
import ExternalDocuments from './components/qms/ExternalDocuments';
import UnregisteredDocuments from './components/qms/UnregisteredDocuments';
import IssueTracker from './components/qms/IssueTracker';
import IssueDetail from './components/qms/IssueDetail';
import EmailRecipientsAdmin from './components/qms/EmailRecipientsAdmin';
import TrainingDashboard from './components/qms/TrainingDashboard';
import UpstreamIssues from './components/UpstreamIssues';
import Presentation from './components/Presentation';
import ContributorRegistration from './components/contributors/ContributorRegistration';
import ContributorDashboard from './components/contributors/ContributorDashboard';
import ContributorReviewUI from './components/contributors/ContributorReviewUI';
import ContributorAdmin from './components/contributors/ContributorAdmin';
import ContributorApply from './components/contributors/ContributorApply';
import ContributorTraining from './components/contributors/ContributorTraining';
import ContributorCertificate from './components/contributors/ContributorCertificate';
import ContributorDetail from './components/contributors/ContributorDetail';
import ContributorShowcase from './components/contributors/ContributorShowcase';
import ApiDocs from './components/ApiDocs';
import CommunityComparison from './components/community/CommunityComparison';
import DocumentHub from './components/DocumentHub';
import FeatureBacklog from './components/features/FeatureBacklog';
import PolicyPortal from './components/contributors/PolicyPortal';
import PolicyView from './components/contributors/PolicyView';
import './app-shell.css';
import TrainingSession from './components/qms/TrainingSession';
import TrainingCertificate from './components/qms/TrainingCertificate';
import DocumentView from './components/Views/DocumentView';

/**
 * Wrap a component with RequireLogin so the route
 * redirects to /login when no auth token is present.
 */
function withLogin(Component) {
  const Wrapped = (props) => (
    <RequireLogin>
      <Component {...props} />
    </RequireLogin>
  );
  Wrapped.displayName = `withLogin(${Component.displayName || Component.name})`;
  return Wrapped;
}

export default function applyConfig(config) {
  config.addonRoutes = [
    ...(config.addonRoutes || []),
    // Home page — redirects to /polyglot (CMS language roots /en, /sv etc. stay CMS-managed)
    { path: '/presentation', component: Presentation, exact: true },
    { path: '/', component: withLogin(HomePage), exact: true },
    // Polyglot translation tools
    { path: '/polyglot', component: LandingPage, exact: true },
    { path: '/polyglot/coverage', component: withLogin(CoverageDashboard), exact: true },
    {
      path: '/polyglot/languages/:code',
      component: withLogin(LanguageDetail),
      exact: true,
    },
    { path: '/polyglot/conventions', component: withLogin(ConventionsBrowser), exact: true },
    {
      path: '/polyglot/conventions/:code',
      component: withLogin(ConventionsDetail),
      exact: true,
    },
    {
      path: '/polyglot/browse/:platform',
      component: withLogin(TranslationBrowser),
      exact: true,
    },
    {
      path: '/polyglot/browse',
      component: withLogin(TranslationBrowser),
      exact: true,
    },
    { path: '/polyglot/messages/:id', component: withLogin(MessageDetail), exact: true },
    { path: '/polyglot/glossary', component: withLogin(GlossarySearch), exact: true },
    {
      path: '/polyglot/verification/:code',
      component: withLogin(VerificationDetail),
      exact: true,
    },
    // API documentation
    { path: '/api-docs', component: withLogin(ApiDocs), exact: true },
    // Document management hub
    { path: '/documents', component: withLogin(DocumentHub), exact: true },
    // Community comparison page (public — no login required, DR-2026-070)
    { path: '/community/corrections', component: CommunityComparison, exact: true },
    // Policy portal (public — no login required)
    { path: '/policies', component: PolicyPortal, exact: true },
    { path: '/policies/:docId', component: PolicyView, exact: true },
    // Contributor system
    { path: '/polyglot/apply', component: ContributorApply, exact: true },
    { path: '/polyglot/register/:token', component: ContributorRegistration, exact: true },
    { path: '/polyglot/training', component: withLogin(ContributorTraining), exact: true },
    { path: '/polyglot/certificate', component: withLogin(ContributorCertificate), exact: true },
    { path: '/polyglot/dashboard', component: withLogin(ContributorDashboard), exact: true },
    { path: '/polyglot/review', component: withLogin(ContributorReviewUI), exact: true },
    { path: '/polyglot/contributors/showcase', component: withLogin(ContributorShowcase), exact: true },
    { path: '/polyglot/contributors', component: withLogin(ContributorAdmin), exact: true },
    { path: '/polyglot/contributors/:id', component: withLogin(ContributorDetail), exact: true },
    // Feature Backlog (DR-2026-068)
    { path: '/polyglot/features', component: withLogin(FeatureBacklog), exact: true },
    // QC Review Queue (DR-2026-051)
    { path: '/polyglot/qc/review', component: withLogin(QCReviewDashboard), exact: true },
    { path: '/polyglot/qc/review/work', component: withLogin(QCReviewQueue), exact: true },
    // QC Findings (DR-2026-045)
    { path: '/polyglot/qc', component: withLogin(QCDashboard), exact: true },
    { path: '/polyglot/qc/findings', component: withLogin(QCFindingsBrowser), exact: true },
    { path: '/polyglot/qc/upstream', component: withLogin(UpstreamIssues), exact: true },
    // Platform changelog from Forgejo history (DR-2026-236)
    { path: '/polyglot/changelog', component: withLogin(ChangelogFeed), exact: true },
    // Nav Group Proposals (DR-2026-173 — Session L content-aware classifier)
    {
      path: '/polyglot/nav-group/proposals',
      component: withLogin(NavGroupProposalsBrowser),
      exact: true,
    },
    // Platform knowledge base (separate from polyglot — platform-level)
    { path: '/docs', component: withLogin(DocBrowser), exact: true },
    { path: '/docs/divergence', component: withLogin(DocDivergence), exact: true },
    { path: '/docs/timeline', component: withLogin(DocTimeline), exact: true },
    { path: '/docs/:slug(.*)/history', component: withLogin(DocHistory) },
    { path: '/docs/:slug(.*)/version', component: withLogin(DocVersionView) },
    { path: '/docs/:slug(.*)/review', component: withLogin(DocDraftReview) },
    { path: '/docs/:slug(.*)/edit', component: withLogin(DocEditor) },
    { path: '/docs/:slug(.*)', component: withLogin(DocViewer) },
    // Quality Management System
    { path: '/qms', component: withLogin(QmsDashboard), exact: true },
    { path: '/qms/register', component: withLogin(DocumentRegister), exact: true },
    { path: '/qms/register/new', component: withLogin(DocumentCreate), exact: true },
    { path: '/qms/register/unregistered', component: withLogin(UnregisteredDocuments), exact: true },
    { path: '/qms/register/:id/edit', component: withLogin(DocumentEditor), exact: true },
    { path: '/qms/register/:id', component: withLogin(DocumentDetail), exact: true },
    { path: '/qms/reviews', component: withLogin(ReviewSchedule), exact: true },
    // DR-2026-297 — the detectors' findings, with persistent dispositions
    { path: '/qms/review-queue', component: withLogin(ReviewQueue), exact: true },
    // DR-2026-352 v0.5.0 — the failure-mode register, read whole; detail by mode id
    { path: '/qms/failure-modes', component: withLogin(FailureModes), exact: true },
    { path: '/qms/failure-modes/:modeId', component: withLogin(FailureModeDetail), exact: true },
    { path: '/qms/external', component: withLogin(ExternalDocuments), exact: true },
    // QMS Issue Tracker
    { path: '/qms/issues', component: withLogin(IssueTracker), exact: true },
    { path: '/qms/issues/:id', component: withLogin(IssueDetail), exact: true },
    // QMS Email Recipients (SPEC-EMAIL-001 / DR-2026-171 Phase A)
    { path: '/qms/recipients', component: withLogin(EmailRecipientsAdmin), exact: true },
    // QMS Training
    { path: '/qms/training', component: withLogin(TrainingDashboard), exact: true },
    { path: '/qms/training/session/:sessionId', component: withLogin(TrainingSession), exact: true },
    { path: '/qms/training/certificate/:certificateId', component: withLogin(TrainingCertificate), exact: true },
  ];

  config.settings.nonContentRoutes = [
    ...(config.settings.nonContentRoutes || []),
    /^\/api-docs(\/.*)?$/,
    /^\/documents(\/.*)?$/,
    /^\/community(\/.*)?$/,
    /^\/policies(\/.*)?$/,
    /^\/polyglot(\/.*)?$/,
    /^\/docs(\/.*)?$/,
    /^\/qms(\/.*)?$/,
  ];

  // Override light theme's Anontools (which depends on Plone's @actions
  // endpoint) with our own that reads the JWT token directly from Redux.
  config.registerSlotComponent({
    slot: 'headerTools',
    name: 'Anontools',
    component: UserTools,
  });

  // App-shell header for platform modules (ARCH-2026-008 Phase A).
  // Renders above the VLT header; on app routes, CSS hides VLT header.
  config.registerSlotComponent({
    slot: 'aboveHeader',
    name: 'AppShell',
    component: AppHeader,
  });

  // App-shell footer for platform modules (ARCH-2026-008 Phase A).
  // Replaces VLT footer links on app routes; CSS hides default post-footer.
  config.registerSlotComponent({
    slot: 'footer',
    name: 'AppFooter',
    component: AppFooter,
  });

  // DR-2026-142: per-page title-block suppression. DocumentView wraps
  // DefaultView, filtering @type:title from blocks at view time when
  // content.suppress_title === true. Field comes from nick's suppress_title
  // behavior (core 1002 for Page, multilingual 1001 for Languageroot,
  // both via patches/). Keys must match nick's actual @type values per
  // View.jsx:171 source-trace; "Document" is not a nick type.
  config.views = {
    ...(config.views || {}),
    contentTypesViews: {
      ...(config.views?.contentTypesViews || {}),
      Page: DocumentView,
      Languageroot: DocumentView,
    },
  };

  return config;
}
